<template>
  <div>
    <FormField :labelText="formFieldLabels.changePaymentMethodOrFrequencyText" required>
      <ul class="radio-group large">
        <li
          v-for="option in [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]"
          :key="option.value"
        >
          <input
            type="radio"
            :id="'accountDetailsOnly-' + option.label"
            :value="option.value"
            :name="'accountDetailsOnly-' + option.label"
            v-model.lazy="v$.accountDetailsOnly.$model"
            @change="handleChangeOptionsSelection()"
            @keyup.enter="accountDetailsOnly = option.value"
          />
          <label :for="'accountDetailsOnly-' + option.label">{{ option.label }}</label>
        </li>
      </ul>
    </FormField>
    <FormField :labelText="formFieldLabels.paymentMethodLabelText" required>
      <ul class="radio-group large">
        <li v-for="method in referenceData.paymentMethodOptions" :key="method.returnValue">
          <input
            type="radio"
            :id="method.returnValue"
            :value="method.returnValue"
            :disabled="!accountDetailsOnly"
            name="paymentMethods"
            v-model.lazy="v$.paymentsObj.paymentMethod.$model"
            @change="reinitialiseSelectedPaymentForm()"
            @keyup.enter="paymentsObj.paymentMethod = method.returnValue"
          />
          <label :for="method.returnValue">{{ method.display }}</label>
        </li>
      </ul>
    </FormField>
    <div class="form-section">
      <template v-if="this.paymentsObj.paymentMethod">
        <FormField
          :labelText="formFieldLabels.frequencyLabelText"
          :error="v$.paymentsObj.paymentFrequency.$error"
          errorMessage="Please include a response for payment frequency."
          required
        >
          <DropdownSelect
            id="paymentFrequency"
            v-model="v$.paymentsObj.paymentFrequency.$model"
            :disabled="!accountDetailsOnly"
            :items="calcPaymentFrequencyOptions.map((option) => ({ value: option.returnValue, name: option.display }))"
            :initialSelect="paymentsObj.paymentFrequency ? paymentsObj.paymentFrequency : 'Please select a payment frequency'"
          />
        </FormField>
      </template>

      <!-- Payment start date for bank & CC -->
      <template v-if="(requiredIfCreditCard() || requiredIfBankAccount()) && accountDetailsOnly">
        <FormField
          :labelText="getPaymentStartDateLabel"
          :key="`start-date-${paymentsObj.paymentMethod}`"
          :required="accountDetailsOnly"
          class="half-width"
          :isDateInput="true"
          placeholder="DD / MM / YYYY"
          v-maska="'## / ## / ####'"
          v-model="v$.paymentsObj.paymentStartDate.$model"
          :disabled="requiredIfCreditCard()"
          :helperText="helperTextForBankOrCC"
          :error="v$.paymentsObj.paymentStartDate.$error"
          :errorMessage="paymentStartDateErrorMsg(v$.paymentsObj.paymentStartDate)"
        />
      </template>

      <!-- Bank acc form -->
      <template v-if="requiredIfBankAccount()">
        <FormField
          labelText="BSB"
          required
          :id="`bsb-${paymentsObj.paymentMethod}`"
          :key="`bsb-${paymentsObj.paymentMethod}`"
          type="string"
          :placeholder="BSB_MASK"
          v-maska="BSB_MASK"
          v-model.trim="v$.paymentsObj.paymentBankBSB.$model"
          class="half-width"
          :error="v$.paymentsObj.paymentBankBSB.$error"
          :errorMessage="calcPaymentBankBSBErrorMessage"
        />
        <FormField
          labelText="Account number"
          required
          type="number"
          :key="`account-number-${paymentsObj.paymentMethod}`"
          placeholder="Enter account number"
          maxlength="10"
          v-model.trim="v$.paymentsObj.paymentBankAccountNumber.$model"
          :error="v$.paymentsObj.paymentBankAccountNumber.$error"
          :errorMessage="calcPaymentBankAccountNumberErrorMessage"
        />
        <FormField
          labelText="Account name"
          :key="`account-name-${paymentsObj.paymentMethod}`"
          required
          placeholder="e.g. John Citizen"
          v-model.trim="v$.paymentsObj.paymentBankAccountName.$model"
          :error="v$.paymentsObj.paymentBankAccountName.$error"
          :errorMessage="calcPaymentBankAccountNameErrorMessage"
        />
      </template>

      <!-- Credit card form -->
      <template v-if="requiredIfCreditCard()">
        <FormField
          labelText="Name on card"
          :key="`name-on-card-${paymentsObj.paymentMethod}`"
          required
          :id="`name-on-card-${paymentsObj.paymentMethod}`"
          type="string"
          placeholder="John Citizen"
          v-model.trim="v$.paymentsObj.nameOnCard.$model"
          :error="v$.paymentsObj.nameOnCard.$error"
          :errorMessage="calcNameOnCardErrorMessage"
        />

        <!-- Ipsi Credit Card form -->
        <FormField>
          <IpsiCreditCardForm @isCreditCardCompleted="isCreditCardCompleted = $event" @fetchCardToken="fetchCardToken" ref="creditCardForm" />
        </FormField>

        <!-- Processing fees text -->
        <!-- <FormField>
          <p v-html="referenceData.premiumPaymentCreditCardPaymentFeeText.informationText"></p>
        </FormField> -->
      </template>

      <!-- Invoice form -->
      <!-- Below form field doesn't render properly when put inside template tags, hence below section is a div. -->
      <div v-else-if="requiredIfInvoice()">
        <!-- Invoice due date -->
        <FormField
          :labelText="getPaymentStartDateLabel"
          required
          class="half-width"
          :error="v$.paymentsObj.paymentStartDate.$error"
          :errorMessage="paymentStartDateErrorMsg(v$.paymentsObj.paymentStartDate)"
          :isDateInput="true"
          placeholder="DD / MM / YYYY"
          v-maska="'## / ## / ####'"
          v-model.lazy.trim="v$.paymentsObj.paymentStartDate.$model"
          :helperText="
            replaceTokenInString(referenceData.premiumPaymentInvoiceCalloutText.informationText, ReplacementTokenEnums.DPT, convertDateStringToText(this.membershipInfo.datePaidTo))
          "
        />

        <p>BPAY biller code</p>
        <p>{{ referenceData.premiumPaymentInvoiceBpayBillerCode.informationText }}</p>
        <p>Customer reference number</p>
        <p>{{ crn }}</p>
      </div>
    </div>

    <FormField class="align-checkbox-top" v-if="requiredIfBankAccount() || requiredIfCreditCard()">
      <div class="field checkbox">
        <input
          type="checkbox"
          id="directDebitServiceAgreement"
          v-model="paymentsObj.isDirectDebitServiceAgreement"
          @change="v$.paymentsObj.isDirectDebitServiceAgreement.$touch()"
          @keyup.enter.space="paymentsObj.isDirectDebitServiceAgreement = !paymentsObj.isDirectDebitServiceAgreement"
        />
        <label
          for="directDebitServiceAgreement"
          v-html="
            addRequiredMark(referenceData.reviewAndSubmitDirectDebitAcknowledgementText.informationText) + referenceData.reviewAndSubmitPrivacyInformationText.informationText
          "
        />
        <label for="directDebitServiceAgreement" class="error" v-if="v$.paymentsObj.isDirectDebitServiceAgreement.$error">Please include a response for Declaration.</label>
      </div>
    </FormField>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { FormField } from '@/components';
import { PaymentMethods, PaymentFrequency, CollectionMethods } from '@general/js/enums';
import { checkDateWithin30Days, formatFormDate, checkDayBefore29, isPastDate, isDateBefore, reverseFormatFormDate, convertDateStringToText } from '@general/js/utils';
import { checkWithinXDays, isDateFormat, isDateInRange, replaceTokenInString, sentenceCase } from '@/utils';
import { addDays, addMonths, format } from 'date-fns';
import { useVuelidate } from '@vuelidate/core';
import { required, numeric, minLength, maxLength, requiredIf } from '@vuelidate/validators';
import { JOIN_NOW_NAME_VALIDATOR, BSB_VALIDATOR } from '@general/js/validators';
import { ErrorStrings } from '@general/js/constants';
import { ReplacementTokenEnums } from '@/enums';
import { DropdownSelect, IpsiCreditCardForm } from '@general/js/components';
import PaymentDetailsMixin from './PaymentDetailsMixin';

// Validate the next payment date if DPT is more than 30 days in the future
const IS_VALID_DPT_GREATER_THAN_30_DAYS = (date, dpt) => {
  const notDPTWithin30Days = !checkWithinXDays(formatFormDate(dpt), 30);
  const formattedDPT = reverseFormatFormDate(dpt);

  if (isDateFormat(date) && notDPTWithin30Days && !isPastDate(formattedDPT)) {
    const nextPaymentDateWithin31DaysOfDPT = isDateInRange(date, new Date(dpt), addDays(new Date(dpt), 30));

    if (nextPaymentDateWithin31DaysOfDPT) {
      return false;
    }
  }
  return true;
};

// Validate the next payment date if DPT is less than or equal to 30 days in the future
const IS_VALID_DPT_LESS_THAN_30_DAYS = (date, dpt) => {
  const dptWithin30Days = checkWithinXDays(dpt, 30);
  const formattedDPT = reverseFormatFormDate(dpt);
  if (isDateFormat(date) && (dptWithin30Days || isPastDate(formattedDPT))) {
    const nextPaymentDateWithin29Days = checkWithinXDays(formatFormDate(date), 29);

    if (nextPaymentDateWithin29Days) {
      return false;
    }
  }
  return true;
};

const BSB_MASK = '### - ###';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'FuturePaymentsForm',

  mixins: [PaymentDetailsMixin],

  components: {
    FormField,
    IpsiCreditCardForm,
    DropdownSelect,
  },

  props: {
    referenceData: {
      type: Object,
    },
    formResponses: {
      type: Object,
    },
    formFieldLabels: {
      type: Object,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    crn: {
      type: String,
    },
    /**
     * This object contains the current bank acc details(before updating the payment method)
     */
    initialData: {
      type: Object,
    },
  },

  data() {
    return {
      paymentsObj: {
        paymentMethod: this.initialData?.premiumPaymentAccount.accountType || null,
        paymentFrequency: sentenceCase(this.initialData?.premiumPaymentMethod.frequency) || null,
        paymentStartDate: null,
        paymentBankBSB: null,
        paymentBankAccountNumber: null,
        paymentBankAccountName: '',
        nameOnCard: '',
        isDirectDebitServiceAgreement: false,
      },
      BSB_MASK,
      ReplacementTokenEnums,
      sttLoadStatus: null,
      isCreditCardCompleted: false,
      accountDetailsOnly: false,
    };
  },

  validations: {
    paymentsObj: {
      paymentMethod: {
        required,
      },
      paymentFrequency: {
        required,
      },
      paymentStartDate: {
        required: requiredIf(function () {
          return this.accountDetailsOnly;
        }),
        isDateFormat(value) {
          if (value !== undefined && value !== null && this.accountDetailsOnly) {
            return isDateFormat(value);
          }
          return true;
        },
        checkDayBefore29(value) {
          if (this.accountDetailsOnly) {
            return checkDayBefore29(value);
          }
          return true;
        },
        isDateAtLeast2DaysFromToday(value) {
          if (this.requiredIfBankAccount() && this.accountDetailsOnly) {
            return !checkWithinXDays(formatFormDate(value), 2);
          }
          return true;
        },
        isDateAfterDpt(value) {
          if (this.requiredIfBankAccount() && this.accountDetailsOnly) {
            return isDateBefore(value, reverseFormatFormDate(this.membershipInfo.datePaidTo));
          }
          return true;
        },
        isWithin30DaysOfDpt(value) {
          if (this.requiredIfBankAccount() && this.accountDetailsOnly) {
            return checkDateWithin30Days(value, new Date(this.membershipInfo.datePaidTo));
          }
          return true;
        },
        IS_VALID_DPT_GREATER_THAN_30_DAYS(value) {
          if (this.requiredIfInvoice() && this.accountDetailsOnly) {
            return IS_VALID_DPT_GREATER_THAN_30_DAYS(value, this.membershipInfo.datePaidTo);
          }
          return true;
        },
        IS_VALID_DPT_LESS_THAN_30_DAYS(value) {
          if (this.requiredIfInvoice() && this.accountDetailsOnly) {
            return IS_VALID_DPT_LESS_THAN_30_DAYS(value, this.membershipInfo.datePaidTo);
          }
          return true;
        },
      },
      nameOnCard: {
        required: requiredIf(function () {
          return this.requiredIfCreditCard();
        }),
        maxLength: maxLength(50),
      },
      isDirectDebitServiceAgreement: {
        required: requiredIf(function () {
          return this.requiredIfCreditCard() || this.requiredIfBankAccount();
        }),
        isSelected: (value, vm) => {
          if (vm.paymentMethod === PaymentMethods.BANK_ACCOUNT || vm.paymentMethod === PaymentMethods.CREDIT_CARD) {
            return value;
          }
          return true;
        },
      },
      paymentBankBSB: {
        required: requiredIf(function () {
          return this.requiredIfBankAccount();
        }),
        BSB_VALIDATOR,
        minLength: minLength(9),
        maxLength: maxLength(9),
      },
      paymentBankAccountNumber: {
        required: requiredIf(function () {
          return this.requiredIfBankAccount();
        }),
        numeric,
        minLength: minLength(6),
        maxLength: maxLength(10),
      },
      paymentBankAccountName: {
        required: requiredIf(function () {
          return this.requiredIfBankAccount();
        }),
        JOIN_NOW_NAME_VALIDATOR,
        maxLength: maxLength(50),
      },
    },
    isCreditCardCompleted: {
      checked(value) {
        return this.requiredIfCreditCard() ? value === true : true;
      },
    },
    accountDetailsOnly: {
      required,
    },
  },

  watch: {
    editMode: {
      immediate: true,
      handler() {
        this.reinitialiseSelectedPaymentForm();
      },
      deep: true,
    },
    /**
     * Watchers fire on page load as well as whenever the watched property changes
     */
    'paymentsObj.paymentFrequency': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.paymentsObj.isDirectDebitServiceAgreement = false;
        this.emitPaymentDetails();
      }
    },
    'paymentsObj.paymentStartDate': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.paymentsObj.isDirectDebitServiceAgreement = false;
        this.emitPaymentDetails();
      }
    },
    'paymentsObj.paymentBankBSB': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.paymentsObj.isDirectDebitServiceAgreement = false;
        this.emitPaymentDetails();
      }
    },
    'paymentsObj.paymentBankAccountNumber': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.paymentsObj.isDirectDebitServiceAgreement = false;
        this.emitPaymentDetails();
      }
    },
    'paymentsObj.paymentBankAccountName': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.paymentsObj.isDirectDebitServiceAgreement = false;
        this.emitPaymentDetails();
      }
    },
    'paymentsObj.nameOnCard': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.paymentsObj.isDirectDebitServiceAgreement = false;
        this.emitPaymentDetails();
      }
    },
    'paymentsObj.isDirectDebitServiceAgreement': function (val) {
      this.paymentsObj.isDirectDebitServiceAgreement = val;
      this.emitPaymentDetails();
    },
    isCreditCardCompleted() {
      this.paymentsObj.isDirectDebitServiceAgreement = false;
      this.emitPaymentDetails();
    },
  },

  computed: {
    calcPaymentFrequencyOptions() {
      // Credit card doesn't have fortnightly frequency
      if (this.paymentsObj.paymentMethod === PaymentMethods.CREDIT_CARD) {
        return this.referenceData.paymentFrequencyOptions.filter((op) => {
          return op.returnValue !== PaymentFrequency.FORTNIGHTLY;
        });
      }
      // Invoice payment method doesn't have fortnightly & monthly frequencies.
      if (this.paymentsObj.paymentMethod === CollectionMethods.INVOICE) {
        return this.referenceData.paymentFrequencyOptions.filter((op) => {
          return ![PaymentFrequency.FORTNIGHTLY, PaymentFrequency.MONTHLY].includes(op.returnValue);
        });
      }
      return this.referenceData.paymentFrequencyOptions;
    },
    calcPaymentBankBSBErrorMessage() {
      if (this.v$.paymentsObj.paymentBankBSB.required.$invalid) {
        return 'Please include a response for BSB.';
      }
      return 'Please enter a 6 digit BSB.';
    },
    calcPaymentBankAccountNumberErrorMessage() {
      if (this.v$.paymentsObj.paymentBankAccountNumber.required.$invalid) {
        return 'Please include a response for Account number.';
      }
      return 'Please enter a minimum of 6 digits for an account number.';
    },
    calcPaymentBankAccountNameErrorMessage() {
      if (this.v$.paymentsObj.paymentBankAccountName.required.$invalid) {
        return 'Please include a response for Account name.';
      }
      if (this.v$.paymentsObj.paymentBankAccountName.maxLength.$invalid) {
        return 'You have exceeded the maximum amount of characters.';
      }
      return "We can't accept that input - please remove numbers, symbols and/or consecutive spaces.";
    },
    calcNameOnCardErrorMessage() {
      if (this.v$.paymentsObj.nameOnCard.maxLength.$invalid) {
        return 'You have exceeded the maximum amount of characters.';
      }
      return 'Please include a response for Name on card.';
    },
    getPaymentStartDateLabel() {
      return this.formFieldLabels.startPaymentsLabelText;
    },
    helperTextForBankOrCC() {
      return this.requiredIfCreditCard()
        ? this.referenceData.premiumPaymentCreditCardCalloutText.informationText
        : replaceTokenInString(
            this.referenceData.premiumPaymentBankAccountCalloutText.informationText,
            ReplacementTokenEnums.DPT,
            convertDateStringToText(this.membershipInfo.datePaidTo),
          );
    },
  },

  mounted() {
    // copy over all values of payments
    Object.assign(this.paymentsObj, this.formResponses);
  },

  methods: {
    convertDateStringToText,
    replaceTokenInString,

    async submitPaymentForm() {
      await this.$refs.creditCardForm.submitAwait();
    },

    fetchCardToken(data) {
      if (data.sttLoadStatus === true) {
        const [month, year] = data.cardExpiryDate.split('/');
        const tokenResponse = {
          creditCardToken: data.token,
          truncatedCard: data.maskedPAN,
          expiryMonth: month,
          expiryYear: year,
          instrumentType: data.instrumentType,
          cardCategory: data.cardCategory,
          cardSubCategory: data.cardSubCategory,
        };

        this.paymentsObj.tokenResponse = tokenResponse;
      } else {
        this.paymentsObj.tokenResponse = null;
        this.reinitialiseSelectedPaymentForm();
      }
      // emit updated stt token data
      this.emitPaymentDetails();
    },

    emitPaymentDetails() {
      const res = cloneDeep(this.paymentsObj);

      const obj = {
        paymentsObj: {
          paymentMethod: res.paymentMethod,
          paymentFrequency: res.paymentFrequency,
          paymentStartDate: res.paymentStartDate,
          paymentBankBSB: res.paymentBankBSB,
          paymentBankAccountNumber: res.paymentBankAccountNumber,
          paymentBankAccountName: res.paymentBankAccountName,
          nameOnCard: res.nameOnCard,
          isDirectDebitServiceAgreement: res.isDirectDebitServiceAgreement,
        },
        isPaymentsInvalid: this.v$.$invalid,
      };
      if (!this.v$.$invalid && res.tokenResponse) {
        obj.paymentsObj.tokenResponse = res.tokenResponse;
      }
      this.$emit('updatePaymentDetails', obj);
    },

    calcCreditCardPaymentStartDate() {
      // Credit card "Next payment date" rules, as listed under the story: DIG-478 Business rules
      let dayOfDPT;
      let monthOfDPT;
      let yearOfDPT;
      if (this.membershipInfo.datePaidTo) {
        const dpt = new Date(this.membershipInfo.datePaidTo);
        dayOfDPT = dpt.getDate();
        monthOfDPT = dpt.getMonth();
        yearOfDPT = dpt.getFullYear();
      }
      const today = new Date();
      const dayToday = today.getDate();
      const monthToday = today.getMonth();
      const yearToday = today.getFullYear();

      let paymentStartDate;

      // if DPT year is prior to this year i.e in past
      if (yearOfDPT < yearToday) {
        if (dayToday < 13) {
          // paymentStartDate = 15 of current month
          paymentStartDate = format(new Date(yearToday, monthToday, 15), 'dd / MM / yyyy');
        } else {
          // paymentStartDate = 15 of current month + 1 i.e next month
          paymentStartDate = format(addMonths(new Date(yearToday, monthToday, 15), 1), 'dd / MM / yyyy');
        }
      }
      // else if DPT year is more than or equal to current year
      else if (yearOfDPT === yearToday) {
        // if DPT month in past
        if (monthOfDPT < monthToday) {
          if (dayToday < 13) {
            // paymentStartDate = 15 of current month
            paymentStartDate = format(new Date(yearToday, monthToday, 15), 'dd / MM / yyyy');
          } else {
            // paymentStartDate = 15 of current month + 1 i.e next month
            paymentStartDate = format(addMonths(new Date(yearToday, monthToday, 15), 1), 'dd / MM / yyyy');
          }
        }
        // else if DPT month in future
        else if (monthOfDPT > monthToday) {
          if (dayOfDPT < 15) {
            // paymentStartDate = 15 of DPT month
            paymentStartDate = format(new Date(yearOfDPT, monthOfDPT, 15), 'dd / MM / yyyy');
          } else {
            // paymentStartDate = 15 of DPT month + 1
            paymentStartDate = format(addMonths(new Date(yearOfDPT, monthOfDPT, 15), 1), 'dd / MM / yyyy');
          }
        }
        // else DPT month === currentMonth
        else {
          // below is required to keep the code easy to read/understand, hence ignoring eslint warning
          // eslint-disable-next-line no-lonely-if
          if (dayOfDPT < 15) {
            if (dayToday < 13) {
              // paymentStartDate = 15 of current month
              paymentStartDate = format(new Date(yearToday, monthToday, 15), 'dd / MM / yyyy');
            }
            // else dayToday >=13
            else {
              // paymentStartDate = 15 of current month + 1 i.e next month
              paymentStartDate = format(addMonths(new Date(yearToday, monthToday, 15), 1), 'dd / MM / yyyy');
            }
          }
          // else dayOfDPT >= 15
          else {
            // paymentStartDate = 15 of current month + 1 i.e next month
            paymentStartDate = format(addMonths(new Date(yearToday, monthToday, 15), 1), 'dd / MM / yyyy');
          }
        }
      }
      // else DPT year is in future (> current year)
      else {
        // below is required to keep the code easy to read/understand, hence ignoring eslint warning
        // eslint-disable-next-line no-lonely-if
        if (dayOfDPT < 15) {
          // paymentStartDate = 15 of DPT month
          paymentStartDate = format(new Date(yearOfDPT, monthOfDPT, 15), 'dd / MM / yyyy');
        } else {
          // paymentStartDate = 15 of DPT month + 1
          paymentStartDate = format(addMonths(new Date(yearOfDPT, monthOfDPT, 15), 1), 'dd / MM / yyyy');
        }
      }

      this.paymentsObj.paymentStartDate = paymentStartDate;
    },

    calcInvoicePaymentStartDate() {
      const within30DaysOfToday = checkWithinXDays(this.membershipInfo.datePaidTo, 30);
      let defaultNextPaymentDate;
      if (within30DaysOfToday) {
        // If DPT is within 30 days of today, then next payment date should be at least 30 days after DPT
        defaultNextPaymentDate = addDays(new Date(), 30);
      } else {
        // If DPT is more than 30 days from today, then next payment date should be at least 31 days after today
        defaultNextPaymentDate = addDays(new Date(this.membershipInfo.datePaidTo), 31);
      }

      if ([29, 30, 31].includes(defaultNextPaymentDate.getDate())) {
        defaultNextPaymentDate = new Date(defaultNextPaymentDate.getFullYear(), defaultNextPaymentDate.getMonth() + 1, 1);
      }
      this.paymentsObj.paymentStartDate = format(defaultNextPaymentDate, 'dd / MM / yyyy');
    },

    paymentStartDateErrorMsg(paymentStartDate) {
      if (paymentStartDate?.isDateFormat) {
        if (paymentStartDate?.checkDayBefore29 === false) {
          return 'Only dates between 1-28 can be accepted. Please select another date.';
        }
        if (paymentStartDate?.IS_VALID_DPT_GREATER_THAN_30_DAYS === false) {
          return 'Please enter a date that is more than 30 days after the date paid to.';
        }
        if (paymentStartDate?.IS_VALID_DPT_LESS_THAN_30_DAYS === false) {
          return 'Please enter a date that is 30 days or more after today’s date.';
        }
        if (paymentStartDate?.isDateAfterDpt === false) {
          return 'Please enter a date that is at least 1 day after the date paid to.';
        }
        if (paymentStartDate?.isDateAtLeast2DaysFromToday === false) {
          return 'Please enter a date that is at least 2 days from today’s date';
        }
        if (paymentStartDate?.isWithin30DaysOfDpt === false) {
          return 'Please enter a date that is less than 30 days from date paid to.';
        }
      }
      return ErrorStrings.PAYMENT_START_DATE_ERROR.INVALID_DATE;
    },

    requiredIfCreditCard() {
      return this.paymentsObj.paymentMethod === PaymentMethods.CREDIT_CARD;
    },

    requiredIfBankAccount() {
      return this.paymentsObj.paymentMethod === PaymentMethods.BANK_ACCOUNT;
    },

    requiredIfInvoice() {
      return this.paymentsObj.paymentMethod === CollectionMethods.INVOICE;
    },

    reinitialiseSelectedPaymentForm() {
      this.paymentsObj.paymentFrequency = sentenceCase(this.initialData?.premiumPaymentMethod.frequency) || null;
      this.paymentsObj.paymentStartDate = null;
      this.paymentsObj.isDirectDebitServiceAgreement = false;

      // Switching payment method type so reset the appropriate data and reinitialise the form
      if (this.paymentsObj.paymentMethod === PaymentMethods.CREDIT_CARD) {
        this.paymentsObj.paymentBankBSB = '';
        this.paymentsObj.paymentBankAccountNumber = '';
        this.paymentsObj.paymentBankAccountName = '';
        this.paymentsObj.nameOnCard = '';

        this.calcCreditCardPaymentStartDate();
      } else if (this.paymentsObj.paymentMethod === PaymentMethods.BANK_ACCOUNT) {
        this.paymentsObj.nameOnCard = '';
        this.paymentsObj.paymentBankBSB = '';
        this.paymentsObj.paymentBankAccountNumber = '';
        this.paymentsObj.paymentBankAccountName = '';
        this.isCreditCardCompleted = false;
      } else if (this.paymentsObj.paymentMethod === CollectionMethods.INVOICE) {
        this.calcInvoicePaymentStartDate();
      }

      this.emitPaymentDetails();
      this.v$?.$reset?.(); // reset form fields error messages
      this.$emit('reinitialiseSelectedPaymentForm');
    },

    handleChangeOptionsSelection() {
      this.paymentsObj.isDirectDebitServiceAgreement = false;

      if (this.accountDetailsOnly === false) {
        this.paymentsObj.paymentMethod = this.initialData?.premiumPaymentAccount.accountType || null;
        this.paymentsObj.paymentFrequency = sentenceCase(this.initialData?.premiumPaymentMethod.frequency) || null;
        this.paymentsObj.paymentStartDate = null;
      }
    },
  },
};
</script>
